<template>
  <div class="kpi-container" >
    <KpiHeader  v-if="showHeader"
                title="My KPI-Overview"
                :view-mode="'grid'"
                :board-setup="boardSetup"
                :wave-data="waveData"
                @view-changed="onKpiHeaderViewChanged"
                @config-click="showConfigDialog = true"
                @add-click="onKpiHeaderAddKpi" />

    <KpiGrid :columns="viewMode === 'grid' ? 3 : 1">
      <template v-for="(control, index) in getControls" :key="control.id">
        <div v-if="control.type === 'linebreak'" class="kpi-grid-break"></div>
        <KpiCardAlternative v-else-if="isSkewed"
                            :board-data="boardData[index]"
                            :wave-data="waveData"
                            :control="control" />
        <KpiCard  v-else
                  :board-data="boardData[index]"
                  :control="control"
                  @click-kpi="onKpiCardClick"
                  @edit-kpi="onKpiCardEdit"
                  @delete-kpi="onKpiCardDelete" />
      </template>

      <!-- Add Button als leeres control -->
      <div  v-if="false && viewMode === 'grid' && boardSetup.controls.length % 3 !== 0"
            class="kpi-add-placeholder"
            :class="{ 'kpi-add-placeholder-skew': isSkewed }" @click="onKpiHeaderAddKpi">
        <div class="placeholder-content">
          <Plus :size="32" class="placeholder-icon" />
          <p class="placeholder-text">{{ $t('Webportal.Kpi.Add')}}</p>
        </div>
      </div>
    </KpiGrid>

    <!-- Zentraler Edit Dialog -->
    <ModalEditKpi v-model="showEditDialog"
                  :kpi="selectedKpi" />

    <ModalGridConfig  v-model="showConfigDialog"
                      :initialConfig="gridConfig"
                      @grid-config-save="onGridConfigSave" />

    <!-- Optional: Delete Confirmation Dialog -->
    <b-modal  v-model="showDeleteDialog"
              title="KPI-Instanz löschen"
              ok-variant="danger"
              ok-title="Löschen"
              cancel-title="Abbrechen"
              @ok="onConfirmDelete">
      <p>Möchten Sie die KPI-Instanz "{{ selectedKpi?.name }}" wirklich löschen?</p>
    </b-modal>
  </div>
</template>

<script>
import KpiHeader from './KpiHeader.vue';
import KpiGrid from './KpiGrid.vue';
import KpiCard from './KpiCard.vue';
import ModalGridConfig from './ModalGridConfig.vue';
import ModalEditKpi from './ModalEditKpi.vue';
import { Plus } from 'lucide-vue-next';
import { useToast } from 'bootstrap-vue-next';
import { mapGetters } from 'vuex';
import { useKpiHelpers } from '@/composables/kpi-helpers';
import KpiCardAlternative from '@/componentsv3/KpiBoard/KpiCardAlternative.vue';

export default {
  name: 'KpiBoard',

  emits: ['add-kpis'],

  provide() {
    return {
      onKpisAdded: (data) => this.handleKpisAdded(data.kpis)
    };
  },

  components: {
    KpiHeader,
    KpiGrid,
    KpiCard,
    KpiCardAlternative,
    ModalGridConfig,
    ModalEditKpi,
    Plus
  },

  props: {
    showHeader:     { type: Boolean, default: true },
    boardData:      { type: Array,  required: true },
    boardSetup:     { type: Object, required: true },
    boardIndex:     { type: Number, required: true },
    waveData:       { type: Object,  required: true }
  },

  setup() {
    const { show } = useToast();
    const kpiHelpers = useKpiHelpers();
    return { showToast: show, kpiHelpers };
  },

  data() {
    return {
      gridConfig: {
        sortBy: 'name',
        sortOrder: 'asc',
        clickToDetails: true
      },
      selectedKpi: null,
      showConfigDialog: false,
      showEditDialog: false,
      showDeleteDialog: false,
      viewMode: 'grid'
    };
  },

  watch: {
    boardData(newVal) {
      console.warn("BB DATA WATCH: ", newVal);
    }
  },

  computed: {
    ...mapGetters('app', [ 'sidebarTheme' ]),

    isSkewed() {
      return this.sidebarTheme === 'skewed';
    },

    kpiSubtitle() {
      const count = this.boardData.length || 0;
      return `${count} KPI-Instanzen konfiguriert`;
    },

    getControls() {
      return this.boardSetup?.boards?.[this.boardIndex]?.controls || [];
    }
  },

  methods: {
    onKpiHeaderViewChanged(newView) {
      this.viewMode = newView;
    },

    onGridConfigSave(config) {
      this.gridConfig = { ...config };

      this.showToast({
        props: {
          title: this.$t("Webportal.ConfigurationSaved"),
          variant: "success",
          autoHide: true,
          delay: 3000,
        }
      });
    },

    onKpiCardClick(kpi) {
      if (this.gridConfig.clickToDetails) {
        // NotImplemented: Navigate to KPI detail view
        // this.$router.push({ name: 'KpiDetail', params: { id: kpi.id } });
      }
    },

    onKpiCardEdit(kpi) {
      this.selectedKpi = kpi;
      this.showEditDialog = true;
    },

    onKpiCardDelete(kpi) {
      this.selectedKpi = kpi;
      this.showDeleteDialog = true;
    },

    onKpiEditSave(updatedKpi) {
      const index = this.kpiData.findIndex(k => k.id === updatedKpi.id);
      if (index !== -1) {
        this.kpiData.splice(index, 1, updatedKpi);
      }

      this.showToast({
        props: {
          title: this.$t("Webportal.KpiUpdated", {
            entity: 'Saved',
          }),
          variant: "success",
          autoHide: true,
          delay: 3000,
        }
      });
    },

    onKpiHeaderAddKpi() {
      this.$emit('add-kpis', this.boardIndex);
    },

    handleKpisAdded(newKpis) {
      this.kpiData = [...newKpis];

      this.showToast({
        props: {
          title: this.$t("Webportal.KpisAdded", {
            count: newKpis.length,
          }),
          variant: "success",
          autoHide: true,
          delay: 3000,
        }
      });

      this.$emit('kpis-added', { kpis: newKpis, index: this.index });
    },

    onConfirmDelete() {
      const index = this.kpiData.findIndex(k => k.id === this.selectedKpi.id);
      if (index !== -1) {
        this.kpiData.splice(index, 1);
      }

      this.showToast({
        props: {
          title: this.$t("Webportal.KpiDeleted", {
            entity: 'Deleted',
          }),
          variant: "success",
          autoHide: true,
          delay: 3000,
        }
      });

      this.selectedKpi = null;
    }
  }
}
</script>

<style lang="scss" scoped>
.kpi-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}
.kpi-add-placeholder-skew {
  border: 1px solid var(--color-border);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transform: skewX(var(--target-skew-angle-inverse));
  position: relative;

  > * {
    transform: skewX(var(--target-skew-angle));
  }

}

.kpi-add-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  border: 2px dashed var(--bs-border-color);
  border-radius: 0.75rem;
  background-color: var(--bs-body-bg);
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    border-color: var(--bs-primary);
    // background-color: var(--color-accent);

    .placeholder-icon {
      color: var(--target-primary);
      transform: scale(1.1);
    }

    .placeholder-text {
      color: var(--target-primary);
    }
  }

  &:active {
    transform: scale(0.98);
  }
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
}

.placeholder-icon {
  color: var(--bs-secondary-color);
  transition: all 0.15s ease-in-out;
}

.placeholder-text {
  font-size: 0.875rem;
  color: var(--bs-secondary-color);
  margin: 0;
  transition: color 0.15s ease-in-out;
}
</style>
